import React, { useMemo } from 'react'
import { List, Box, Typography, CircularProgress } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import SearchCitation from './SearchCitation'
import GroupedSearchCitation from './GroupedSearchCitation'
import DocumentScannerOutlinedIcon from '@mui/icons-material/DocumentScannerOutlined'
import SearchOffOutlinedIcon from '@mui/icons-material/SearchOffOutlined'
import ErrorOutlineOutlinedIcon from '@mui/icons-material/ErrorOutlineOutlined'
import { useSearchTokens } from '../searchDesignTokens'

export const SearchCitations = () => {
  const { t } = useTranslation()
  const dk = useSearchTokens()

  const error = useSelector(state => state.search.error)
  const isLoadingSearchCitations = useSelector(state => state.search.isLoadingSearchCitations)
  const filteredCitations = useSelector(state => state.search.filteredCitations || [])
  const totalResultCount = useSelector(state => state.search.totalResultCount)
  const searchFilters = useSelector(state => state.search.searchFilters)

  // Group citations by document_id
  const groupedCitations = useMemo(() => {
    if (!filteredCitations || filteredCitations.length === 0) return []

    const groups = {}
    const order = []

    filteredCitations.forEach((citation) => {
      const docId = citation?.metadata?.document_id || citation?.id || `ungrouped-${Math.random()}`

      if (!groups[docId]) {
        groups[docId] = {
          docId,
          citations: [],
        }
        order.push(docId)
      }
      groups[docId].citations.push(citation)
    })

    return order.map(id => groups[id])
  }, [filteredCitations])

  if (isLoadingSearchCitations) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100%',
          p: 4
        }}
      >
        <CircularProgress size={40} thickness={4} sx={{ color: dk.gold, mb: 2 }} />
        <Typography variant="body2" sx={{ color: dk.text2, fontWeight: 500 }}>
          {t('searching_documents')}
        </Typography>
      </Box>
    )
  }

  if (error) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100%',
          p: 4
        }}
      >
        <ErrorOutlineOutlinedIcon sx={{ color: dk.error, fontSize: '2.5rem', mb: 2 }} />
        <Typography align="center" sx={{ color: dk.text2 }}>
          {error}
        </Typography>
      </Box>
    )
  }

  if (totalResultCount === 0 && !isLoadingSearchCitations) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100%',
          p: 4
        }}
      >
        <DocumentScannerOutlinedIcon sx={{ color: dk.disabled, fontSize: '3rem', mb: 2 }} />
        <Typography align="center" sx={{ color: dk.text2, fontWeight: 500 }}>
          {t('search_to_find_documents')}
        </Typography>
      </Box>
    )
  }

  if (filteredCitations.length === 0) {
    const hasActiveFilters = searchFilters && Object.keys(searchFilters).some(
      key => Array.isArray(searchFilters[key]) ? searchFilters[key].length > 0 : !!searchFilters[key]
    )
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100%',
          p: 4
        }}
      >
        <SearchOffOutlinedIcon sx={{ color: dk.disabled, fontSize: '3rem', mb: 2 }} />
        <Typography align="center" sx={{ color: dk.text2, fontWeight: 500 }}>
          {t('no_results')}
        </Typography>
        {hasActiveFilters && (
          <Typography variant="body2" align="center" sx={{ color: dk.text3, mt: 1 }}>
            {t('try_removing_filters')}
          </Typography>
        )}
      </Box>
    )
  }

  return (
    <List
      sx={{
        flexGrow: 1,
        p: 1,
      }}
    >
      {groupedCitations.map((group) => {
        if (group.citations.length === 1) {
          const citation = group.citations[0]
          return <SearchCitation key={citation.id || group.docId} citation={citation} />
        }
        return (
          <GroupedSearchCitation
            key={group.docId}
            docId={group.docId}
            citations={group.citations}
          />
        )
      })}
    </List>
  )
}

export default SearchCitations
